import { z } from "zod";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
const monthStr = z.string().regex(/^\d{4}-\d{2}$/, "expected YYYY-MM");

export const ChildSchema = z.object({
  id: z.string(),
  name_en: z.string().min(1),
  name_zh: z.string().min(1),
  // DOB nullable — Vera doesn't always have it on enrolment; she fills in when known.
  date_of_birth: isoDate.nullable(),
  enrolled_on: isoDate,
  ended_on: isoDate.nullable(),
  parent_contact: z.string(),
  // gp_alias = the term Vera uses in Google Photos face/person search.
  // Often a nickname differing from the formal name. Null until she fills it in.
  gp_alias: z.string().nullable(),
});
export type Child = z.infer<typeof ChildSchema>;

export const ReportPhotoSchema = z.object({
  gp_media_id: z.string(),
  gp_thumbnail_url: z.string().url(),
  is_main: z.boolean(),
  // ISO date YYYY-MM-DD of when the photo was taken, derived from
  // Google Photos Picker's createTime. Null for legacy reports
  // captured before this field was tracked.
  taken_at: z.string().nullable().optional(),
});
export type ReportPhoto = z.infer<typeof ReportPhotoSchema>;

export const ReportContentSchema = z.object({
  story_en: z.string(),
  story_zh: z.string(),
  analysis_en: z.string(),
  analysis_zh: z.string(),
  learning_outcomes_en: z.string(),
  learning_outcomes_zh: z.string(),
  what_next_en: z.string(),
  what_next_zh: z.string(),
  evaluation_en: z.string().nullable(),
  evaluation_zh: z.string().nullable(),
  eylf_codes: z.array(z.string()),
});

export const ReportStatus = z.enum(["draft", "approved", "rendered"]);
export type ReportStatusValue = z.infer<typeof ReportStatus>;

export const ReportSchema = z.object({
  id: z.string(),
  child_id: z.string(),
  report_period: monthStr,
  recorded_by: z.string(),
  status: ReportStatus,
  vera_framing_note: z.string().nullable(),
  prior_report_id: z.string().nullable(),
  photos: z.array(ReportPhotoSchema),
  content: ReportContentSchema,
  follow_up_date: isoDate,
  ai_draft_raw: z.unknown().nullable(),
  pdf_storage_path: z.string().nullable(),
  created_at: z.number(),
  updated_at: z.number(),
  approved_at: z.number().nullable(),
  rendered_at: z.number().nullable(),
});
export type Report = z.infer<typeof ReportSchema>;

export function emptyContent(): z.infer<typeof ReportContentSchema> {
  return {
    story_en: "", story_zh: "",
    analysis_en: "", analysis_zh: "",
    learning_outcomes_en: "", learning_outcomes_zh: "",
    what_next_en: "", what_next_zh: "",
    evaluation_en: null, evaluation_zh: null,
    eylf_codes: [],
  };
}

export function monthKey(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function addOneMonth(period: string): string {
  const [y, m] = period.split("-").map(Number) as [number, number];
  const next = new Date(Date.UTC(y, m, 1));
  return monthKey(next);
}

export function followUpDate(period: string): string {
  return `${addOneMonth(period)}-01`;
}

export function ageDisplay(dob: string | null, on: Date = new Date()): string {
  if (!dob) return "?";
  const [by, bm, bd] = dob.split("-").map(Number) as [number, number, number];
  const birth = new Date(Date.UTC(by, bm - 1, bd));
  let years = on.getUTCFullYear() - birth.getUTCFullYear();
  let months = on.getUTCMonth() - birth.getUTCMonth();
  if (on.getUTCDate() < birth.getUTCDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  return `${years}y${months}m`;
}
